import type { Hotspot, HotspotMedia } from "../../api/types.js";
import type {
	HotspotScreenPosition,
	HotspotsSnapshot,
} from "../../hotspots/types.js";
import { t } from "../../i18n/init.js";
import { renderMarkdownToFragment } from "../../viewer/markdown.js";
import type { ComponentInstance } from "../component.js";
import { setText } from "../dom-helpers.js";
import { openHotspotLightbox } from "./hotspot-lightbox.js";

export interface HotspotPopupProps {
	hotspots: readonly Hotspot[];
	snapshot: HotspotsSnapshot;
	// Position of the active hotspot dot in screen space. May be `undefined`
	// when positions haven't been emitted yet.
	activePosition: HotspotScreenPosition | undefined;
	viewport: { width: number; height: number };
}

export interface HotspotPopupCallbacks {
	onClose: () => void;
	onPrev: () => void;
	onNext: () => void;
}

const POPUP_WIDTH_FALLBACK = 320;
const POPUP_HEIGHT_FALLBACK = 180;
const ANCHOR_OFFSET_PX = 24;
const VIEWPORT_MARGIN_PX = 12;

function positionPopup(
	popup: HTMLElement,
	active: HotspotScreenPosition | undefined,
	viewport: { width: number; height: number },
): void {
	if (active === undefined || !active.visible) {
		// Fallback: center the popup. Fly-to animation (4s) rotates the camera so
		// the dot becomes visible; until then the popup floats at viewport center.
		popup.style.left = "50%";
		popup.style.top = "50%";
		popup.style.transform = "translate(-50%, -50%)";
		return;
	}
	const popupW = popup.offsetWidth || POPUP_WIDTH_FALLBACK;
	const popupH = popup.offsetHeight || POPUP_HEIGHT_FALLBACK;
	const left = Math.max(
		VIEWPORT_MARGIN_PX,
		Math.min(
			viewport.width - popupW - VIEWPORT_MARGIN_PX,
			active.x - popupW / 2,
		),
	);
	const top = Math.max(
		VIEWPORT_MARGIN_PX,
		Math.min(
			viewport.height - popupH - VIEWPORT_MARGIN_PX,
			active.y - popupH - ANCHOR_OFFSET_PX,
		),
	);
	popup.style.left = `${left}px`;
	popup.style.top = `${top}px`;
	popup.style.transform = "none";
}

export function mountHotspotPopup(
	root: HTMLElement,
	initial: HotspotPopupProps,
	callbacks: HotspotPopupCallbacks,
): ComponentInstance<HotspotPopupProps> {
	// Track the element that had focus before opening so we can restore on close.
	let lastFocused: HTMLElement | null = null;

	const wrap = document.createElement("div");
	wrap.classList.add("viewer-ui-interactive");
	wrap.setAttribute("role", "dialog");
	// aria-modal="false" — popup is non-blocking, the user can still orbit
	// the canvas while it is open.
	wrap.setAttribute("aria-modal", "false");
	wrap.style.cssText = [
		"position:fixed",
		`width:${POPUP_WIDTH_FALLBACK}px`,
		"background:rgba(15,15,17,0.85)",
		"backdrop-filter:blur(8px)",
		"border:1px solid rgba(255,255,255,0.10)",
		"border-radius:1rem",
		"box-shadow:0 12px 40px rgba(0,0,0,0.45)",
		"overflow:hidden",
		"z-index:12",
		"display:none",
		"pointer-events:auto",
		"color:#f4f4f5",
		"font-family:system-ui,sans-serif",
	].join(";");

	// Header strip with badge + close button.
	const header = document.createElement("div");
	header.style.cssText =
		"display:flex;align-items:center;justify-content:space-between;padding:0.75rem 1rem 0";

	const badge = document.createElement("span");
	badge.style.cssText =
		"font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem;color:#d8b4fe;background:rgba(168,85,247,0.18);padding:0.125rem 0.5rem;border-radius:9999px";
	header.appendChild(badge);

	const closeBtn = document.createElement("button");
	closeBtn.type = "button";
	closeBtn.classList.add("viewer-ui-tap-target");
	closeBtn.setAttribute("aria-label", t("viewer.hotspot.close_label"));
	closeBtn.style.cssText =
		"width:1.75rem;height:1.75rem;border-radius:9999px;background:rgba(0,0,0,0.40);color:#f4f4f5;border:0;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:1rem;line-height:1";
	closeBtn.textContent = "×";
	closeBtn.addEventListener("click", () => callbacks.onClose());
	header.appendChild(closeBtn);

	wrap.appendChild(header);

	// Body: title + description.
	const body = document.createElement("div");
	body.style.cssText = "padding:0.5rem 1rem 0";

	const title = document.createElement("h3");
	title.id = "hotspot-title";
	title.style.cssText =
		"margin:0 0 0.25rem;font-size:0.95rem;font-weight:600;color:#ffffff";
	body.appendChild(title);

	// Inc E — description is now sanitized Markdown (block container, not <p>,
	// so lists/paragraphs can nest). Rendered via renderMarkdownToFragment.
	const description = document.createElement("div");
	description.style.cssText =
		"margin:0;font-size:0.75rem;line-height:1.55;color:#a1a1aa;word-break:break-word";
	body.appendChild(description);

	// Inc E — gallery: image + prev/next + dots + caption. Hidden when no media.
	const gallery = document.createElement("div");
	gallery.style.cssText = "margin-top:0.625rem;display:none";

	const galleryFrame = document.createElement("div");
	galleryFrame.style.cssText =
		"position:relative;border-radius:0.5rem;overflow:hidden;background:rgba(0,0,0,0.35)";

	const galleryImg = document.createElement("img");
	galleryImg.loading = "lazy";
	galleryImg.decoding = "async";
	galleryImg.setAttribute("role", "button");
	galleryImg.tabIndex = 0;
	galleryImg.setAttribute("aria-label", t("viewer.hotspot.gallery_open_label"));
	galleryImg.style.cssText =
		"display:block;width:100%;height:auto;max-height:11rem;object-fit:cover;cursor:zoom-in";
	galleryFrame.appendChild(galleryImg);

	const galleryNavStyle =
		"position:absolute;top:50%;transform:translateY(-50%);width:1.75rem;height:1.75rem;border-radius:9999px;background:rgba(0,0,0,0.55);color:#fff;border:0;cursor:pointer;font-size:1rem;line-height:1;align-items:center;justify-content:center";
	const galleryPrev = document.createElement("button");
	galleryPrev.type = "button";
	galleryPrev.classList.add("viewer-ui-tap-target");
	galleryPrev.setAttribute(
		"aria-label",
		t("viewer.hotspot.gallery_prev_label"),
	);
	galleryPrev.textContent = "‹";
	galleryPrev.style.cssText = `${galleryNavStyle};left:0.375rem;display:flex`;
	galleryFrame.appendChild(galleryPrev);

	const galleryNext = document.createElement("button");
	galleryNext.type = "button";
	galleryNext.classList.add("viewer-ui-tap-target");
	galleryNext.setAttribute(
		"aria-label",
		t("viewer.hotspot.gallery_next_label"),
	);
	galleryNext.textContent = "›";
	galleryNext.style.cssText = `${galleryNavStyle};right:0.375rem;display:flex`;
	galleryFrame.appendChild(galleryNext);

	gallery.appendChild(galleryFrame);

	const galleryDots = document.createElement("div");
	galleryDots.style.cssText =
		"display:flex;gap:0.25rem;justify-content:center;margin-top:0.375rem";
	gallery.appendChild(galleryDots);

	const galleryCaption = document.createElement("div");
	galleryCaption.style.cssText =
		"margin-top:0.25rem;font-size:0.6875rem;color:#a1a1aa;text-align:center";
	gallery.appendChild(galleryCaption);

	body.appendChild(gallery);

	wrap.appendChild(body);

	// Footer: prev/next nav (cyclic).
	const footer = document.createElement("div");
	footer.style.cssText =
		"display:flex;align-items:center;justify-content:space-between;padding:0.75rem 1rem 1rem;gap:0.5rem;margin-top:0.5rem";

	const prevBtn = document.createElement("button");
	prevBtn.type = "button";
	prevBtn.classList.add("viewer-ui-tap-target");
	prevBtn.setAttribute("aria-label", t("viewer.hotspot.prev_label"));
	prevBtn.textContent = t("viewer.hotspot.prev_text");
	prevBtn.style.cssText =
		"background:transparent;color:#d4d4d8;border:1px solid rgba(255,255,255,0.10);border-radius:9999px;padding:0.25rem 0.625rem;font-size:0.6875rem;cursor:pointer;font-family:inherit";
	prevBtn.addEventListener("click", () => callbacks.onPrev());
	footer.appendChild(prevBtn);

	const counter = document.createElement("span");
	counter.style.cssText =
		"font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem;color:#a1a1aa";
	footer.appendChild(counter);

	const nextBtn = document.createElement("button");
	nextBtn.type = "button";
	nextBtn.classList.add("viewer-ui-tap-target");
	nextBtn.setAttribute("aria-label", t("viewer.hotspot.next_label"));
	nextBtn.textContent = t("viewer.hotspot.next_text");
	nextBtn.style.cssText = prevBtn.style.cssText;
	nextBtn.addEventListener("click", () => callbacks.onNext());
	footer.appendChild(nextBtn);

	wrap.appendChild(footer);

	// Keyboard navigation: ArrowLeft / ArrowRight only fire when focus is
	// inside the popup, so they don't interfere with orbit drag elsewhere.
	wrap.addEventListener("keydown", (ev) => {
		if (ev.key === "ArrowLeft") {
			ev.preventDefault();
			callbacks.onPrev();
		} else if (ev.key === "ArrowRight") {
			ev.preventDefault();
			callbacks.onNext();
		}
	});

	root.appendChild(wrap);

	let galleryMedia: readonly HotspotMedia[] = [];
	let galleryIndex = 0;
	// Cache keys so description (Markdown) + gallery rebuild ONLY when the hotspot
	// or its text changes — apply() runs every frame (position events) and
	// re-rendering would destroy focus inside Markdown links / gallery controls.
	let lastDescKey: string | null = null;
	let lastGalleryKey: string | null = null;

	const showGalleryImage = (idx: number): void => {
		if (galleryMedia.length === 0) return;
		galleryIndex =
			((idx % galleryMedia.length) + galleryMedia.length) % galleryMedia.length;
		const m = galleryMedia[galleryIndex];
		if (m === undefined) return;
		galleryImg.src = m.url;
		galleryImg.alt =
			m.caption !== null && m.caption.length > 0
				? m.caption
				: t("viewer.hotspot.gallery_image_alt", { n: galleryIndex + 1 });
		if (m.caption !== null && m.caption.length > 0) {
			galleryCaption.textContent = m.caption;
			galleryCaption.style.display = "block";
		} else {
			galleryCaption.textContent = "";
			galleryCaption.style.display = "none";
		}
		const dots = galleryDots.children;
		for (let i = 0; i < dots.length; i++) {
			const dot = dots[i];
			if (dot instanceof HTMLElement) {
				dot.style.background =
					i === galleryIndex ? "#d8b4fe" : "rgba(255,255,255,0.30)";
			}
		}
	};

	const buildGallery = (media: readonly HotspotMedia[]): void => {
		galleryMedia = media;
		galleryIndex = 0;
		galleryDots.replaceChildren();
		if (media.length === 0) {
			gallery.style.display = "none";
			galleryImg.removeAttribute("src");
			return;
		}
		gallery.style.display = "block";
		const multi = media.length > 1;
		galleryPrev.style.display = multi ? "flex" : "none";
		galleryNext.style.display = multi ? "flex" : "none";
		if (multi) {
			for (let i = 0; i < media.length; i++) {
				const dot = document.createElement("span");
				dot.style.cssText =
					"width:0.375rem;height:0.375rem;border-radius:9999px;background:rgba(255,255,255,0.30)";
				galleryDots.appendChild(dot);
			}
		}
		showGalleryImage(0);
	};

	const openLightbox = (): void => {
		if (galleryMedia.length > 0)
			openHotspotLightbox(galleryMedia, galleryIndex);
	};

	// Gallery controls stop propagation so the popup's Arrow handler (hotspot
	// prev/next) does not fire while navigating the gallery.
	galleryPrev.addEventListener("click", (ev) => {
		ev.stopPropagation();
		showGalleryImage(galleryIndex - 1);
	});
	galleryNext.addEventListener("click", (ev) => {
		ev.stopPropagation();
		showGalleryImage(galleryIndex + 1);
	});
	galleryImg.addEventListener("click", openLightbox);
	galleryImg.addEventListener("keydown", (ev) => {
		if (ev.key === "Enter" || ev.key === " ") {
			ev.preventDefault();
			openLightbox();
		}
	});
	gallery.addEventListener("keydown", (ev) => {
		if (ev.key === "ArrowLeft") {
			ev.stopPropagation();
			ev.preventDefault();
			showGalleryImage(galleryIndex - 1);
		} else if (ev.key === "ArrowRight") {
			ev.stopPropagation();
			ev.preventDefault();
			showGalleryImage(galleryIndex + 1);
		}
	});

	let currentlyOpen = false;

	const apply = (props: HotspotPopupProps): void => {
		const isOpen = props.snapshot.openIndex >= 0;
		if (!isOpen) {
			if (currentlyOpen) {
				// Restore focus to the dot that opened the popup, if still in DOM.
				const fallback = lastFocused;
				wrap.style.display = "none";
				currentlyOpen = false;
				if (fallback && document.contains(fallback)) {
					try {
						fallback.focus();
					} catch {
						/* ignore */
					}
				}
			}
			return;
		}
		const idx = props.snapshot.openIndex;
		const hs = props.hotspots[idx];
		if (hs === undefined) {
			wrap.style.display = "none";
			currentlyOpen = false;
			return;
		}
		const wasOpen = currentlyOpen;
		if (!wasOpen) {
			lastFocused = (document.activeElement as HTMLElement | null) ?? null;
			currentlyOpen = true;
		}
		wrap.style.display = "block";
		wrap.setAttribute("aria-labelledby", "hotspot-title");
		setText(badge, `#${idx + 1}`);
		setText(title, hs.title);
		// Inc E: render Markdown + (re)build gallery only when the hotspot or its
		// description changes (apply runs per-frame).
		const descKey = `${hs.id}::${hs.description ?? ""}`;
		if (descKey !== lastDescKey) {
			lastDescKey = descKey;
			description.replaceChildren(
				renderMarkdownToFragment(hs.description ?? ""),
			);
		}
		// Key on hotspot id + media signature so the gallery rebuilds when the
		// same open hotspot receives updated media (studio upload/delete reload).
		const galleryKey = `${hs.id}::${(hs.media ?? []).map((m) => m.id).join(",")}`;
		if (galleryKey !== lastGalleryKey) {
			lastGalleryKey = galleryKey;
			buildGallery(hs.media ?? []);
		}
		setText(
			counter,
			t("viewer.hotspot.counter", {
				current: idx + 1,
				total: props.hotspots.length,
			}),
		);
		positionPopup(wrap, props.activePosition, props.viewport);
		// Move focus ONLY on the closed → open transition. Subsequent updates
		// (per-frame hotspot-positions events, prev/next while popup is already
		// open) must NOT steal focus, otherwise prev/next/Tab navigation breaks.
		if (!wasOpen) {
			try {
				closeBtn.focus();
			} catch {
				/* ignore */
			}
		}
	};

	apply(initial);

	return {
		update(props: HotspotPopupProps) {
			apply(props);
		},
		cleanup() {
			wrap.remove();
		},
	};
}
